import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDashboardStore } from './useDashboardStore';
import { NAV_ITEMS } from './Sidebar';
import { cn } from '@/lib/cn';
import {
  PencilIcon,
  CheckIcon,
  PlusIcon,
  ArrowRightIcon,
  Bars3Icon,
} from '@heroicons/react/24/outline';

// ── Soft tint pairs: icon bg + icon text + hover ring ─────────
const TINT_MAP: Record<string, { bg: string; text: string; ring: string }> = {
  'bg-indigo-500': { bg: 'bg-indigo-50', text: 'text-indigo-600', ring: 'ring-indigo-200' },
  'bg-blue-500': { bg: 'bg-blue-50', text: 'text-blue-600', ring: 'ring-blue-200' },
  'bg-violet-500': { bg: 'bg-violet-50', text: 'text-violet-600', ring: 'ring-violet-200' },
  'bg-amber-500': { bg: 'bg-amber-50', text: 'text-amber-600', ring: 'ring-amber-200' },
  'bg-emerald-500': { bg: 'bg-emerald-50', text: 'text-emerald-600', ring: 'ring-emerald-200' },
  'bg-cyan-500': { bg: 'bg-cyan-50', text: 'text-cyan-600', ring: 'ring-cyan-200' },
  'bg-orange-500': { bg: 'bg-orange-50', text: 'text-orange-600', ring: 'ring-orange-200' },
  'bg-rose-500': { bg: 'bg-rose-50', text: 'text-rose-600', ring: 'ring-rose-200' },
  'bg-teal-500': { bg: 'bg-teal-50', text: 'text-teal-600', ring: 'ring-teal-200' },
  'bg-gray-500': { bg: 'bg-gray-100', text: 'text-gray-600', ring: 'ring-gray-200' },
  'bg-primary-600': { bg: 'bg-indigo-50', text: 'text-indigo-600', ring: 'ring-indigo-200' },
};

const DEFAULT_TINT = { bg: 'bg-gray-100', text: 'text-gray-600', ring: 'ring-gray-200' };

export default function MobileShortcuts() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const shortcuts = useDashboardStore((s) => s.shortcuts);
  const toggleShortcut = useDashboardStore((s) => s.toggleShortcut);
  const reorderShortcuts = useDashboardStore((s) => s.reorderShortcuts);
  const editing = useDashboardStore((s) => s.shortcutsEditing);
  const setEditing = useDashboardStore((s) => s.setShortcutsEditing);

  // Drag state
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const dragNode = useRef<HTMLDivElement | null>(null);

  const shortcutable = NAV_ITEMS.filter((item) => item.path !== '/');
  const activeShortcuts = shortcuts
    .map((path) => shortcutable.find((item) => item.path === path))
    .filter(Boolean) as typeof shortcutable;
  const inactiveShortcuts = shortcutable.filter((item) => !shortcuts.includes(item.path));

  const handleDragStart = useCallback((idx: number, e: React.DragEvent<HTMLDivElement>) => {
    setDragIndex(idx);
    dragNode.current = e.currentTarget;
    e.dataTransfer.effectAllowed = 'move';
    // Make the drag image slightly transparent
    requestAnimationFrame(() => {
      if (dragNode.current) dragNode.current.style.opacity = '0.4';
    });
  }, []);

  const handleDragOver = useCallback((idx: number, e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setOverIndex(idx);
  }, []);

  const handleDragEnd = useCallback(() => {
    if (dragIndex !== null && overIndex !== null && dragIndex !== overIndex) {
      reorderShortcuts(dragIndex, overIndex);
    }
    if (dragNode.current) dragNode.current.style.opacity = '1';
    setDragIndex(null);
    setOverIndex(null);
    dragNode.current = null;
  }, [dragIndex, overIndex, reorderShortcuts]);

  return (
    <div className="mb-6">
      {/* Header row */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
          {t('dashboard.quickActions', 'Quick Actions')}
        </h2>
        <button
          onClick={() => setEditing(!editing)}
          className={cn(
            'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-all',
            editing
              ? 'bg-indigo-600 text-white shadow-sm'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100',
          )}
        >
          {editing ? (
            <>
              <CheckIcon className="h-3.5 w-3.5" />
              {t('common.done', 'Done')}
            </>
          ) : (
            <>
              <PencilIcon className="h-3.5 w-3.5" />
              {t('common.edit', 'Edit')}
            </>
          )}
        </button>
      </div>

      {/* Shortcut grid */}
      {!editing ? (
        /* ── Normal mode: clean card buttons ── */
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 gap-3">
          {activeShortcuts.map((item) => {
            const Icon = item.icon;
            const tint = TINT_MAP[item.color] ?? DEFAULT_TINT;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={cn(
                  'group flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3',
                  'shadow-sm hover:shadow-md hover:border-gray-300',
                  'active:scale-[0.98]',
                  'transition-all duration-200',
                )}
              >
                <div
                  className={cn(
                    'flex h-9 w-9 items-center justify-center rounded-lg shrink-0',
                    tint.bg,
                    tint.text,
                  )}
                >
                  <Icon className="h-5 w-5" />
                </div>
                <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900 truncate">
                  {t(item.label)}
                </span>
                <ArrowRightIcon className="h-3.5 w-3.5 ml-auto shrink-0 text-gray-300 group-hover:text-gray-500 transition-colors" />
              </button>
            );
          })}
          {activeShortcuts.length === 0 && (
            <div className="col-span-full flex flex-col items-center justify-center py-8 text-gray-400 rounded-xl border-2 border-dashed border-gray-200">
              <PlusIcon className="h-6 w-6 mb-2" />
              <p className="text-sm">{t('dashboard.addShortcuts', 'Tap Edit to add shortcuts')}</p>
            </div>
          )}
        </div>
      ) : (
        /* ── Edit mode: toggle buttons on/off ── */
        <div className="space-y-4">
          {/* Active shortcuts — draggable */}
          {activeShortcuts.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-400 mb-2 uppercase tracking-wider">
                {t('dashboard.activeShortcuts', 'Active')}
              </p>
              <div className="space-y-1.5">
                {activeShortcuts.map((item, idx) => {
                  const Icon = item.icon;
                  const tint = TINT_MAP[item.color] ?? DEFAULT_TINT;
                  const isDragging = dragIndex === idx;
                  const isOver = overIndex === idx && dragIndex !== idx;
                  return (
                    <div
                      key={item.path}
                      draggable
                      onDragStart={(e) => handleDragStart(idx, e)}
                      onDragOver={(e) => handleDragOver(idx, e)}
                      onDragEnd={handleDragEnd}
                      onDragLeave={() => setOverIndex(null)}
                      className={cn(
                        'flex items-center gap-2 rounded-xl px-3 py-2.5',
                        'bg-white border border-gray-200',
                        'transition-all duration-150',
                        isDragging && 'opacity-40',
                        isOver && 'border-indigo-400 bg-indigo-50/50 shadow-sm',
                      )}
                    >
                      {/* Drag handle */}
                      <div className="shrink-0 cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 transition-colors">
                        <Bars3Icon className="h-4 w-4" />
                      </div>

                      {/* Icon + label */}
                      <div
                        className={cn(
                          'flex h-8 w-8 items-center justify-center rounded-lg shrink-0',
                          tint.bg,
                          tint.text,
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </div>
                      <span className="text-sm font-medium text-gray-700 truncate flex-1">{t(item.label)}</span>

                      {/* Remove button */}
                      <button
                        onClick={() => toggleShortcut(item.path)}
                        className="flex h-6 w-6 items-center justify-center rounded-md shrink-0 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all duration-150"
                        aria-label="Remove"
                      >
                        <PlusIcon className="h-4 w-4 rotate-45" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Inactive shortcuts */}
          {inactiveShortcuts.length > 0 && (
            <div>
              <p className="text-xs font-medium text-gray-400 mb-2 uppercase tracking-wider">
                {t('dashboard.availableShortcuts', 'Available')}
              </p>
              <div className="space-y-1.5">
                {inactiveShortcuts.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.path}
                      onClick={() => toggleShortcut(item.path)}
                      className={cn(
                        'flex items-center gap-3 rounded-xl px-3 py-2.5 w-full',
                        'bg-gray-50 border border-dashed border-gray-200 text-gray-500',
                        'hover:border-gray-300 hover:bg-white hover:text-gray-700',
                        'active:scale-[0.99] transition-all duration-150',
                      )}
                    >
                      <div
                        className={cn(
                          'flex h-8 w-8 items-center justify-center rounded-lg shrink-0',
                          'bg-gray-100 text-gray-400',
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </div>
                      <span className="text-sm font-medium truncate flex-1 text-left">{t(item.label)}</span>
                      <PlusIcon className="h-4 w-4 shrink-0 text-gray-300" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
